// src/ui/screens/char-select/CharacterDetailPane.tsx — P5 D-26 detail pane.
//
// Mounted when a grid card is tapped. z-index 160 (05-UI-SPEC overlay stack —
// above HUD overlays + BootSplash, below UpdateToast/RoundSetup).
//
// Stats rendered as named descriptors + 4-tier bar visualizations per D-27.
// Five stat rows in order: Hiding, Sight, Hearing, Speed, Nerve. Each row has
// a label, the descriptor word, and a 4-segment filled-up-to-tier bar that
// echoes the stamina-bar aesthetic from the live HUD (UI-SPEC parity cue).
//
// Copy contract (05-UI-SPEC §Copywriting Contract):
//   — Back chevron: "←" at 44x44 touch target, top-left.
//   — Play CTA: literal "Play as {inGameName}"; 56px tall, amber fill.
//   — role="dialog" + aria-modal="true" so screen-readers announce modality.

import type { JSX } from 'react';

import type { Character } from '../../../shared/content/types';
import { resolvePortrait } from '../../assets/portraits';

import {
  hideSkillDescriptor, hideSkillTier,
  sightDescriptor, sightTier,
  hearingDescriptor, hearingTier,
  speedDescriptor, speedTier,
  fearDescriptor, fearTier,
  type DescriptorTier,
} from './descriptors';

export interface CharacterDetailPaneProps {
  character: Character;
  onBack: () => void;
  onConfirm: (id: string) => void;
}

interface StatRow {
  label: string;
  desc: string;
  tier: DescriptorTier;
}

export function CharacterDetailPane({
  character,
  onBack,
  onConfirm,
}: CharacterDetailPaneProps): JSX.Element {
  const rows: readonly StatRow[] = [
    {
      label: 'Hiding',
      desc: hideSkillDescriptor(character.hideSkill),
      tier: hideSkillTier(character.hideSkill),
    },
    {
      label: 'Sight',
      desc: sightDescriptor(character.see.stat),
      tier: sightTier(character.see.stat),
    },
    {
      label: 'Hearing',
      desc: hearingDescriptor(character.hear.stat),
      tier: hearingTier(character.hear.stat),
    },
    {
      label: 'Speed',
      desc: speedDescriptor(character.baseSpeedMultiplier, character.sprintMultiplier),
      tier: speedTier(character.baseSpeedMultiplier, character.sprintMultiplier),
    },
    {
      label: 'Nerve',
      desc: fearDescriptor(character.fear.overall),
      tier: fearTier(character.fear.overall),
    },
  ];

  return (
    <div
      className="character-detail"
      data-testid={`character-detail-${character.id}`}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="character-detail__back"
        data-testid="detail-back"
        onClick={onBack}
        aria-label="Back to character grid"
      >
        {'\u2190'}
      </button>

      <img
        className="character-detail__portrait"
        src={resolvePortrait(character.portraitId)}
        alt={`Portrait of ${character.inGameName}`}
      />
      <h1 className="character-detail__name">{character.inGameName}</h1>

      <div className="character-detail__stats">
        {rows.map((row) => (
          <div
            key={row.label}
            className="character-detail__stat-row"
            data-testid={`stat-${row.label.toLowerCase()}`}
          >
            <span className="character-detail__stat-label">{row.label}</span>
            <span className="character-detail__stat-desc">{row.desc}</span>
            <div className="character-detail__stat-bars" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={
                    'character-detail__stat-bar' +
                    (i <= row.tier ? ' character-detail__stat-bar--filled' : '')
                  }
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="character-detail__quirk">{character.quirk}</p>
      <p className="character-detail__bio">{character.bio}</p>

      <button
        type="button"
        className="character-detail__cta"
        data-testid="play-as-cta"
        onClick={() => onConfirm(character.id)}
      >
        Play as {character.inGameName}
      </button>
    </div>
  );
}
